<template>
  <div v-if="item" class="forms-elements">
    <Breadcrumbs v-bind:breadcrumbs="breadcrumbs" />

    <Widget v-if="item">
      <b-row>
        <b-col lg="6" md="6" sm="12">
          <legend>
            <strong>{{ $t('basicData') }}</strong>
          </legend>

          <b-form-group
            horizontal
            :label="$t('description')"
            label-for="description"
            label-class="text-md-right"
            :label-cols="4"
            breakpoint="md"
          >
            <b-form-textarea
              v-model="item.description"
              :placeholder="$t('description')"
              rows="2"
              max-rows="8"
            />
          </b-form-group>

          <b-form-group
            horizontal
            :label="$t('wholesaleNr')"
            label-for="wholesaleNr"
            label-class="text-md-right"
            :label-cols="4"
            breakpoint="md"
          >
            <b-form-input v-model="item.wholesaleNr" type="text" :placeholder="$t('wholesaleNr')" />
          </b-form-group>

          <b-form-group
            horizontal
            :label="$t('industryNr')"
            label-for="industryNr"
            label-class="text-md-right"
            :label-cols="4"
            breakpoint="md"
          >
            <b-form-input v-model="item.industryNr" type="text" :placeholder="$t('industryNr')" />
          </b-form-group>

          <b-form-group
            horizontal
            :label="$t('supplier')"
            label-for="supplier"
            label-class="text-md-right"
            :label-cols="4"
            breakpoint="md"
          >
            <b-form-input v-model="item.supplier" type="text" :placeholder="$t('supplier')" />
          </b-form-group>

          <b-form-group
            horizontal
            :label="$t('unit')"
            label-for="unit"
            label-class="text-md-right"
            :label-cols="4"
            breakpoint="md"
          >
            <multiselect
              :placeholder="$t('unit')"
              v-model="item.unit"
              id="unit"
              :options="getEnumValues('ItemUnit')"
              :allow-empty="false"
              :show-labels="false"
              deselectLabel
            >
              <template slot="singleLabel" slot-scope="{ option }">
                {{ $t(option) }}
              </template>
              <template slot="option" slot-scope="{ option }">
                {{ $t(option) }}
              </template>
            </multiselect>
          </b-form-group>

          <b-form-group
            horizontal
            :label="$t('type')"
            label-for="type"
            label-class="text-md-right"
            :label-cols="4"
            breakpoint="md"
          >
            <multiselect
              :placeholder="$t('type')"
              v-model="item.type"
              id="type"
              :options="getEnumValues('ItemType')"
              :allow-empty="false"
              :show-labels="false"
              deselectLabel
            >
              <template slot="singleLabel" slot-scope="{ option }">
                {{ $t(option) }}
              </template>
              <template slot="option" slot-scope="{ option }">
                {{ $t(option) }}
              </template>
            </multiselect>
          </b-form-group>

          <b-form-group
            horizontal
            :label="$t('configurationState')"
            label-for="configurationState"
            label-class="text-md-right"
            :label-cols="4"
            breakpoint="md"
          >
            <SelectButton
              id="configurationState"
              v-model="item.configurationState"
              :options="getEnumValues('YesNo')"
            >
              <template #option="props">
                {{ $t(props.option) }}
              </template>
            </SelectButton>
          </b-form-group>
          <b-form-group
            horizontal
            :label="$t('solarPlant')"
            label-for="solarPlant"
            label-class="text-md-right"
            :label-cols="4"
            breakpoint="md"
          >
            <multiselect
              :placeholder="$t('solarPlant')"
              v-model="item.solarPlant"
              id="type"
              :options="getEnumValues('ItemSolarPlant')"
              :allow-empty="false"
              :show-labels="false"
              deselectLabel
            >
              <template slot="singleLabel" slot-scope="{ option }">
                {{ $t(option) }}
              </template>
              <template slot="option" slot-scope="{ option }">
                {{ $t(option) }}
              </template>
            </multiselect>
          </b-form-group>
        </b-col>

        <b-col lg="6" md="6" sm="12">
          <legend>
            <strong>{{ $t('pricing') }}</strong>
          </legend>

          <b-form-group
            horizontal
            :label="$t('purchasePrice')"
            label-for="price"
            label-class="text-md-right"
            :label-cols="4"
            breakpoint="md"
          >
            <InputNumber
              v-model="item.price"
              mode="currency"
              currency="EUR"
              locale="de-DE"
              :placeholder="$t('price')"
              :minFractionDigits="2"
              :maxFractionDigits="2"
              class="w-100"
            />
          </b-form-group>

          <b-form-group
            horizontal
            :label="$t('installationTimeInMinutes')"
            label-for="installationTimeInMinutes"
            label-class="text-md-right"
            :label-cols="4"
            breakpoint="md"
          >
            <InputNumber
              v-model="item.installationTimeInMinutes"
              mode="decimal"
              :minFractionDigits="2"
              :maxFractionDigits="2"
              :placeholder="$t('installationTimeInMinutes')"
              suffix=" min"
              class="w-100"
            />
          </b-form-group>

          <hr />

          <b-form-group
            horizontal
            :label="$t('itemMarkup')"
            label-for="itemMarkup"
            label-class="text-md-right"
            :label-cols="4"
            breakpoint="md"
          >
            <div class="form-control-plaintext">
              {{ formatCurrency(item.priceCalculation?.itemMarkup) }}
            </div>
            <small class="text-muted">
              {{ $t('price') }} × {{ itemSettings.margin || 0 }}% {{ $t('margin') }}
            </small>
          </b-form-group>

          <b-form-group
            horizontal
            :label="$t('itemProfitMarkup')"
            label-for="itemProfitMarkup"
            label-class="text-md-right"
            :label-cols="4"
            breakpoint="md"
          >
            <div class="form-control-plaintext">
              {{ formatCurrency(item.priceCalculation?.itemProfitMarkup) }}
            </div>
            <small class="text-muted">
              ({{ $t('price') }} + {{ $t('itemMarkup') }}) × {{ itemSettings.profitMargin || 0 }}%
              {{ $t('profitMargin') }}
            </small>
          </b-form-group>

          <b-form-group
            horizontal
            :label="$t('itemCustomerPrice')"
            label-for="itemCustomerPrice"
            label-class="text-md-right"
            :label-cols="4"
            breakpoint="md"
          >
            <div class="form-control-plaintext">
              {{ formatCurrency(item.priceCalculation?.itemCustomerPrice) }}
            </div>
            <small class="text-muted">
              {{ $t('price') }} + {{ $t('itemMarkup') }} + {{ $t('itemProfitMarkup') }}
            </small>
          </b-form-group>

          <hr />

          <b-form-group
            horizontal
            :label="$t('laborCost')"
            label-for="laborCost"
            label-class="text-md-right"
            :label-cols="4"
            breakpoint="md"
          >
            <div class="form-control-plaintext">
              {{ formatCurrency(item.priceCalculation?.laborCost) }}
            </div>
            <small class="text-muted">
              {{ $t('installationTimeInMinutes') }} × {{ itemSettings.hourlyRate || 0 }} €/h
              {{ $t('hourlyRate') }}
            </small>
          </b-form-group>

          <b-form-group
            horizontal
            :label="$t('laborMarkup')"
            label-for="laborMarkup"
            label-class="text-md-right"
            :label-cols="4"
            breakpoint="md"
          >
            <div class="form-control-plaintext">
              {{ formatCurrency(item.priceCalculation?.laborMarkup) }}
            </div>
            <small class="text-muted">
              {{ $t('laborCost') }} × {{ itemSettings.generalBusinessCosts || 0 }}%
              {{ $t('generalBusinessCosts') }}
            </small>
          </b-form-group>

          <b-form-group
            horizontal
            :label="$t('laborProfitMarkup')"
            label-for="laborProfitMarkup"
            label-class="text-md-right"
            :label-cols="4"
            breakpoint="md"
          >
            <div class="form-control-plaintext">
              {{ formatCurrency(item.priceCalculation?.laborProfitMarkup) }}
            </div>
            <small class="text-muted">
              ({{ $t('laborCost') }} + {{ $t('laborMarkup') }}) ×
              {{ itemSettings.profitMargin || 0 }}% {{ $t('profitMargin') }}
            </small>
          </b-form-group>

          <b-form-group
            horizontal
            :label="$t('laborCustomerPrice')"
            label-for="laborCustomerPrice"
            label-class="text-md-right"
            :label-cols="4"
            breakpoint="md"
          >
            <div class="form-control-plaintext">
              {{ formatCurrency(item.priceCalculation?.laborCustomerPrice) }}
            </div>
            <small class="text-muted">
              {{ $t('laborCost') }} + {{ $t('laborMarkup') }} + {{ $t('laborProfitMarkup') }}
            </small>
          </b-form-group>

          <hr />

          <b-form-group
            horizontal
            :label="$t('totalCustomerPrice')"
            label-for="totalCustomerPrice"
            label-class="text-md-right font-weight-bold"
            :label-cols="4"
            breakpoint="md"
          >
            <div class="form-control-plaintext font-weight-bold">
              {{ formatCurrency(item.priceCalculation?.totalCustomerPrice) }}
            </div>
            <small class="text-muted">
              {{ $t('itemCustomerPrice') }} + {{ $t('laborCustomerPrice') }}
            </small>
          </b-form-group>

          <b-form-group
            horizontal
            :label="$t('price') + ' ' + $t('calculatedAt')"
            label-for="calculatedAt"
            label-class="text-md-right"
            :label-cols="4"
            breakpoint="md"
          >
            <div class="form-control-plaintext text-muted">
              {{ formatDateTime(item.priceCalculation?.calculatedAt) }}
            </div>
          </b-form-group>
        </b-col>
      </b-row>

      <!-- Second Row: Technical Data and Documents -->
      <b-row>
        <!-- Technical Data Column -->
        <b-col lg="6" md="6" sm="12" v-if="item.solarPlant !== 'NO'">
          <legend>
            <strong>{{ $t('technicalData') }}</strong>
          </legend>

          <!-- BATTERY specific fields -->
          <div v-if="item.solarPlant === 'BATTERY'">
            <b-form-group
              horizontal
              :label="$t('manufacturer')"
              label-for="manufacturer"
              label-class="text-md-right"
              :label-cols="4"
              breakpoint="md"
            >
              <b-form-input
                v-model="item.battery.manufacturer"
                type="text"
                :placeholder="$t('manufacturer')"
              />
            </b-form-group>

            <b-form-group
              horizontal
              :label="$t('type')"
              label-for="type"
              label-class="text-md-right"
              :label-cols="4"
              breakpoint="md"
            >
              <b-form-input v-model="item.battery.type" type="text" :placeholder="$t('type')" />
            </b-form-group>
            <b-form-group
              horizontal
              :label="$t('capacity_kwh')"
              label-for="capacity"
              label-class="text-md-right"
              :label-cols="4"
              breakpoint="md"
            >
              <b-form-input
                v-model="item.battery.capacity"
                mode="decimal"
                :minFractionDigits="3"
                suffix=" kWh"
                locale="de-DE"
                :placeholder="$t('capacity_kwh')"
              />
            </b-form-group>
            <b-form-group
              horizontal
              :label="$t('dischargePower')"
              label-for="dischargePower"
              label-class="text-md-right"
              :label-cols="4"
              breakpoint="md"
            >
              <b-form-input
                v-model="item.battery.dischargePower"
                mode="decimal"
                :minFractionDigits="3"
                suffix=" kW"
                locale="de-DE"
                :placeholder="$t('dischargePower')"
              />
            </b-form-group>
            <b-form-group
              horizontal
              :label="$t('batteryModuleCount')"
              label-for="batteryModuleCount"
              label-class="text-md-right"
              :label-cols="4"
              breakpoint="md"
            >
              <b-form-input
                v-model="item.battery.batteryModuleCount"
                mode="decimal"
                locale="de-DE"
                :placeholder="$t('batteryModuleCount')"
              />
            </b-form-group>

            <b-form-group
              horizontal
              :label="$t('batteryModuleCapacity')"
              label-for="batteryModuleCapacity"
              label-class="text-md-right"
              :label-cols="4"
              breakpoint="md"
            >
              <b-form-input
                v-model="item.battery.batteryModuleCapacity"
                mode="decimal"
                :minFractionDigits="3"
                suffix=" kWh"
                locale="de-DE"
                :placeholder="$t('batteryModuleCapacity')"
              />
            </b-form-group>

            <b-form-group
              horizontal
              :label="$t('usableCapacity')"
              label-for="usableCapacity"
              label-class="text-md-right"
              :label-cols="4"
              breakpoint="md"
            >
              <b-form-input
                v-model="item.battery.usableCapacity"
                mode="decimal"
                :minFractionDigits="3"
                suffix=" kWh"
                locale="de-DE"
                :placeholder="$t('usableCapacity')"
              />
            </b-form-group>

            <b-form-group
              horizontal
              :label="$t('ratedVoltage')"
              label-for="ratedVoltage"
              label-class="text-md-right"
              :label-cols="4"
              breakpoint="md"
            >
              <b-form-input
                v-model="item.battery.ratedVoltage"
                mode="decimal"
                :minFractionDigits="3"
                suffix=" V"
                locale="de-DE"
                :placeholder="$t('ratedVoltage')"
              />
            </b-form-group>
            <b-form-group
              horizontal
              :label="$t('connectionType')"
              label-for="connectionType"
              label-class="text-md-right"
              :label-cols="4"
              breakpoint="md"
            >
              <multiselect
                :placeholder="$t('connectionType')"
                v-model="item.battery.connectionType"
                :options="getBatteryConnectionTypes"
                :show-labels="false"
                :allow-empty="false"
              >
                <template slot="singleLabel" slot-scope="{ option }">{{ $t(option) }}</template>
                <template slot="option" slot-scope="{ option }">
                  {{ $t(option) }}
                </template>
              </multiselect>
            </b-form-group>
          </div>

          <!-- SOLAR_PANELS specific fields -->
          <div v-if="item.solarPlant === 'SOLAR_PANELS'">
            <b-form-group
              horizontal
              :label="$t('manufacturer')"
              label-for="manufacturer"
              label-class="text-md-right"
              :label-cols="4"
              breakpoint="md"
            >
              <b-form-input
                v-model="item.solarPanel.manufacturer"
                type="text"
                :placeholder="$t('manufacturer')"
              />
            </b-form-group>
            <b-form-group
              horizontal
              :label="$t('type')"
              label-for="type"
              label-class="text-md-right"
              :label-cols="4"
              breakpoint="md"
            >
              <b-form-input v-model="item.solarPanel.type" type="text" :placeholder="$t('type')" />
            </b-form-group>
            <b-form-group
              horizontal
              :label="$t('power')"
              label-for="power"
              label-class="text-md-right"
              :label-cols="4"
              breakpoint="md"
            >
              <b-form-input
                v-model="item.solarPanel.power"
                type="text"
                :placeholder="$t('power')"
              />
            </b-form-group>
          </div>

          <!-- INVERTER specific fields -->
          <div v-if="item.solarPlant === 'INVERTER'">
            <b-form-group
              horizontal
              :label="$t('manufacturer')"
              label-for="manufacturer"
              label-class="text-md-right"
              :label-cols="4"
              breakpoint="md"
            >
              <b-form-input
                v-model="item.inverter.manufacturer"
                type="text"
                :placeholder="$t('manufacturer')"
              />
            </b-form-group>
            <b-form-group
              horizontal
              :label="$t('type')"
              label-for="type"
              label-class="text-md-right"
              :label-cols="4"
              breakpoint="md"
            >
              <b-form-input v-model="item.inverter.type" type="text" :placeholder="$t('type')" />
            </b-form-group>
            <b-form-group
              horizontal
              :label="$t('nominalPower')"
              label-for="nominalPower"
              label-class="text-md-right"
              :label-cols="4"
              breakpoint="md"
            >
              <b-form-input
                v-model="item.inverter.nominalPower"
                mode="decimal"
                :minFractionDigits="3"
                suffix=" kW"
                locale="de-DE"
                :placeholder="$t('nominalPower')"
              />
            </b-form-group>
            <b-form-group
              horizontal
              :label="$t('apparentPower')"
              label-for="apparentPower"
              label-class="text-md-right"
              :label-cols="4"
              breakpoint="md"
            >
              <b-form-input
                v-model="item.inverter.apparentPower"
                mode="decimal"
                :minFractionDigits="3"
                suffix=" kW"
                locale="de-DE"
                :placeholder="$t('apparentPower')"
              />
            </b-form-group>
            <b-form-group
              horizontal
              :label="$t('activePower')"
              label-for="activePower"
              label-class="text-md-right"
              :label-cols="4"
              breakpoint="md"
            >
              <b-form-input
                v-model="item.inverter.activePower"
                mode="decimal"
                :minFractionDigits="3"
                suffix=" kW"
                locale="de-DE"
                :placeholder="$t('activePower')"
              />
            </b-form-group>
            <b-form-group
              horizontal
              :label="$t('ratedCurrent')"
              label-for="ratedCurrent"
              label-class="text-md-right"
              :label-cols="4"
              breakpoint="md"
            >
              <b-form-input
                v-model="item.inverter.ratedCurrent"
                mode="decimal"
                :minFractionDigits="3"
                suffix=" A"
                locale="de-DE"
                :placeholder="$t('ratedCurrent')"
              />
            </b-form-group>
            <b-form-group
              horizontal
              :label="$t('shortCircuitCurrent')"
              label-for="shortCircuitCurrent"
              label-class="text-md-right"
              :label-cols="4"
              breakpoint="md"
            >
              <b-form-input
                v-model="item.inverter.shortCircuitCurrent"
                mode="decimal"
                :minFractionDigits="3"
                suffix=" A"
                locale="de-DE"
                :placeholder="$t('shortCircuitCurrent')"
              />
            </b-form-group>
            <b-form-group
              horizontal
              :label="$t('nominalDcPower')"
              label-for="nominalDcPower"
              label-class="text-md-right"
              :label-cols="4"
              breakpoint="md"
            >
              <b-form-input
                v-model="item.inverter.nominalDcPower"
                mode="decimal"
                :minFractionDigits="3"
                suffix=" kW"
                locale="de-DE"
                :placeholder="$t('nominalDcPower')"
              />
            </b-form-group>
            <b-form-group
              horizontal
              :label="$t('allowedChargeDischargeCurrent')"
              label-for="allowedChargeDischargeCurrent"
              label-class="text-md-right"
              :label-cols="4"
              breakpoint="md"
            >
              <b-form-input
                v-model="item.inverter.allowedChargeDischargeCurrent"
                mode="decimal"
                :minFractionDigits="3"
                suffix=" A"
                locale="de-DE"
                :placeholder="$t('allowedChargeDischargeCurrent')"
              />
            </b-form-group>
            <b-form-group
              horizontal
              :label="$t('nominalVoltage')"
              label-for="nominalVoltage"
              label-class="text-md-right"
              :label-cols="4"
              breakpoint="md"
            >
              <b-form-input
                v-model="item.inverter.nominalVoltage"
                mode="decimal"
                :minFractionDigits="3"
                suffix=" V"
                locale="de-DE"
                :placeholder="$t('nominalVoltage')"
              />
            </b-form-group>
            <b-form-group
              horizontal
              :label="$t('gridAndPlantProtectionIntegrated')"
              label-for="gridAndPlantProtectionIntegrated"
              label-class="text-md-right"
              :label-cols="4"
              breakpoint="md"
            >
              <b-form-input
                v-model="item.inverter.gridAndPlantProtectionIntegrated"
                mode="decimal"
                :minFractionDigits="1"
                locale="de-DE"
                :placeholder="$t('gridAndPlantProtectionIntegrated')"
              />
            </b-form-group>

            <b-form-group
              horizontal
              :label="$t('gridAndPlantProtectionType')"
              label-for="gridAndPlantProtectionType"
              label-class="text-md-right"
              :label-cols="4"
              breakpoint="md"
            >
              <multiselect
                :placeholder="$t('gridAndPlantProtectionType')"
                v-model="item.inverter.gridAndPlantProtectionType"
                :options="getGridAndPlantProtectionTypes"
                :show-labels="false"
                :allow-empty="false"
              >
                <template slot="singleLabel" slot-scope="{ option }">{{ $t(option) }}</template>
                <template slot="option" slot-scope="{ option }">
                  {{ $t(option) }}
                </template>
              </multiselect>
            </b-form-group>
          </div>

          <!-- ENERGY_FLOW_SENSOR specific fields -->
          <div v-if="item.solarPlant === 'ENERGY_FLOW_SENSOR'">
            <b-form-group
              horizontal
              :label="$t('manufacturer')"
              label-for="manufacturer"
              label-class="text-md-right"
              :label-cols="4"
              breakpoint="md"
            >
              <b-form-input
                v-model="item.energyFlowDirectionSensor.manufacturer"
                type="text"
                :placeholder="$t('manufacturer')"
              />
            </b-form-group>
            <b-form-group
              horizontal
              :label="$t('type')"
              label-for="type"
              label-class="text-md-right"
              :label-cols="4"
              breakpoint="md"
            >
              <b-form-input
                v-model="item.energyFlowDirectionSensor.type"
                type="text"
                :placeholder="$t('type')"
              />
            </b-form-group>
          </div>

          <!-- WALL_BOX specific fields -->
          <div v-if="item.solarPlant === 'WALL_BOX'">
            <b-form-group
              horizontal
              :label="$t('manufacturer')"
              label-for="manufacturer"
              label-class="text-md-right"
              :label-cols="4"
              breakpoint="md"
            >
              <b-form-input
                v-model="item.wallbox.manufacturer"
                type="text"
                :placeholder="$t('manufacturer')"
              />
            </b-form-group>
            <b-form-group
              horizontal
              :label="$t('type')"
              label-for="type"
              label-class="text-md-right"
              :label-cols="4"
              breakpoint="md"
            >
              <b-form-input v-model="item.wallbox.type" type="text" :placeholder="$t('type')" />
            </b-form-group>
            <b-form-group
              horizontal
              :label="$t('maxNetInputPower')"
              label-for="maxNetInputPower"
              label-class="text-md-right"
              :label-cols="4"
              breakpoint="md"
            >
              <b-form-input
                v-model="item.wallbox.maxNetInputPower"
                mode="decimal"
                :minFractionDigits="3"
                suffix=" kW"
                locale="de-DE"
                :placeholder="$t('maxNetInputPower')"
              />
            </b-form-group>
            <b-form-group
              horizontal
              :label="$t('maxNetOutputPower')"
              label-for="maxNetOutputPower"
              label-class="text-md-right"
              :label-cols="4"
              breakpoint="md"
            >
              <b-form-input
                v-model="item.wallbox.maxNetOutputPower"
                mode="decimal"
                :minFractionDigits="3"
                suffix=" kW"
                locale="de-DE"
                :placeholder="$t('maxNetOutputPower')"
              />
            </b-form-group>
            <b-form-group
              horizontal
              :label="$t('minChargingPower')"
              label-for="minChargingPower"
              label-class="text-md-right"
              :label-cols="4"
              breakpoint="md"
            >
              <b-form-input
                v-model="item.wallbox.minChargingPower"
                mode="decimal"
                :minFractionDigits="3"
                suffix=" kW"
                locale="de-DE"
                :placeholder="$t('minChargingPower')"
              />
            </b-form-group>
            <b-form-group
              horizontal
              :label="$t('maxChargingPower')"
              label-for="maxChargingPower"
              label-class="text-md-right"
              :label-cols="4"
              breakpoint="md"
            >
              <b-form-input
                v-model="item.wallbox.maxChargingPower"
                mode="decimal"
                :minFractionDigits="3"
                suffix=" kW"
                locale="de-DE"
                :placeholder="$t('maxChargingPower')"
              />
            </b-form-group>

            <b-form-group
              horizontal
              :label="$t('activePowerControllable')"
              label-for="activePowerControllable"
              label-class="text-md-right"
              :label-cols="4"
              breakpoint="md"
            >
              <multiselect
                :placeholder="$t('activePowerControllable')"
                v-model="item.wallbox.activePowerControllable"
                :options="optionsYesNo"
                :show-labels="false"
                :allow-empty="false"
              >
                <template slot="singleLabel" slot-scope="{ option }"
                  ><b-badge :variant="yesNoColor(option)">{{ $t(option) }}</b-badge></template
                >
                <template slot="option" slot-scope="{ option }">
                  <b-badge :variant="yesNoColor(option)">
                    {{ $t(option) }}
                  </b-badge></template
                >
              </multiselect>
            </b-form-group>

            <b-form-group
              horizontal
              :label="$t('chargingType')"
              label-for="chargingType"
              label-class="text-md-right"
              :label-cols="4"
              breakpoint="md"
            >
              <multiselect
                :placeholder="$t('chargingType')"
                v-model="item.wallbox.chargingType"
                :options="getEnumValues('ChargingType')"
                :show-labels="false"
                :allow-empty="false"
              >
                <template slot="singleLabel" slot-scope="{ option }">{{ $t(option) }}</template>
                <template slot="option" slot-scope="{ option }">
                  {{ $t(option) }}
                </template>
              </multiselect>
            </b-form-group>

            <b-form-group
              horizontal
              :label="$t('connectionType')"
              label-for="connectionType"
              label-class="text-md-right"
              :label-cols="4"
              breakpoint="md"
            >
              <multiselect
                :placeholder="$t('connectionType') + ' ' + $t('select')"
                v-model="item.wallbox.connectionTypes"
                :options="getEnumValues('WallboxConnectionType')"
                :close-on-select="false"
                :clear-on-select="false"
                :multiple="true"
                :taggable="true"
                :show-labels="true"
                :allow-empty="true"
                :selectLabel="$t('select')"
                :selectedLabel="$t('selected')"
                :deselectLabel="$t('remove_selection')"
              >
                <template slot="singleLabel" slot-scope="{ option }">{{ $t(option) }}</template>
                <template slot="option" slot-scope="{ option }">
                  {{ $t(option) }}
                </template>
                <template slot="tag" slot-scope="{ option }">
                  <b-badge>
                    {{ $t(option) }}
                  </b-badge>
                </template>
              </multiselect>
            </b-form-group>

            <b-form-group
              horizontal
              :label="$t('shiftFactorCosphiType')"
              label-for="shiftFactorCosphiType"
              label-class="text-md-right"
              :label-cols="4"
              breakpoint="md"
            >
              <multiselect
                :placeholder="$t('shiftFactorCosphiType')"
                v-model="item.wallbox.shiftFactorCosphiType"
                :options="getEnumValues('ShiftFactorCosphiType')"
                :show-labels="false"
                :allow-empty="false"
              >
                <template slot="singleLabel" slot-scope="{ option }">{{ $t(option) }}</template>
                <template slot="option" slot-scope="{ option }">
                  {{ $t(option) }}
                </template>
              </multiselect>
            </b-form-group>
          </div>
        </b-col>

        <!-- Documents Column -->
        <b-col
          :lg="item.solarPlant !== 'NO' ? 6 : 12"
          :md="item.solarPlant !== 'NO' ? 6 : 12"
          sm="12"
          class="mb-4"
        >
          <legend>
            <strong>{{ $t('documents') }}</strong>
          </legend>
          <AttachmentGallery
            :attachments="item.itemAttachments"
            v-on:attachment-list:delete="onDeleteItemAttachment"
            :sortByName="false"
            :showDeleteButton="isAdmin"
          ></AttachmentGallery>

          <DropzoneUpload
            v-if="!isNew"
            :complete="isAttachmentUploadComplete"
            :parentId="item._id"
            parentType="itemAttachments"
            :uploadCallback="uploadItemAttachment"
            v-on:dropzone-upload:reset="isAttachmentUploadComplete = false"
          />
          <div v-else class="text-muted">
            <small
              >{{ $t('save') }} Sie zunächst das {{ $t('item') }}, um
              {{ $t('documents') }} hochzuladen.</small
            >
          </div>
        </b-col>
      </b-row>

      <b-row>
        <b-col lg="12" md="12" sm="12">
          <ButtonBar
            :showPreviousButton="buttons.showPreviousButton"
            :showNextButton="buttons.showNextButton"
            :showDeleteButton="buttons.showDeleteButton"
            :model="item"
            modelEditRoute="ItemEditPage"
            modelListRoute="ItemsPage"
            modelRouteParamName="itemNumber"
            :nextModelNumber="item.nextItemNumber"
            :previousModelNumber="item.previousItemNumber"
            :updateCallback="updateItem"
            :createCallback="createItem"
            :deleteCallback="deleteItem"
            :fetchCallback="fetchItems"
          />
        </b-col>
      </b-row>
    </Widget>
  </div>
</template>

<script>
import Widget from '@/components/Widget/Widget';
import Breadcrumbs from '@/components/Breadcrumbs';
import { mapGetters, mapActions } from 'vuex';
import { yesNo } from '@/helpers/enums';
import Multiselect from 'vue-multiselect';
import ButtonBar from '@/components/ButtonBar.vue';
import DropzoneUpload from '@/components/DropzoneUpload';
import AttachmentGallery from '@/components/AttachmentGallery';
import { getYesNoStateColor } from '@/helpers/colors';
import SelectButton from 'primevue/selectbutton';
import InputNumber from 'primevue/inputnumber';

export default {
  name: 'ItemEditPage',
  components: {
    Widget,
    Breadcrumbs,
    Multiselect,
    ButtonBar,
    AttachmentGallery,
    DropzoneUpload,
    SelectButton,
    InputNumber,
  },
  props: {
    itemNumber: {
      required: false,
      default: null,
    },
  },
  data() {
    return {
      number: null,
      buttonsDisabled: false,
      isAttachmentUploadComplete: false,
      itemAttachments: [],
    };
  },
  computed: {
    ...mapGetters('auth', ['isAdmin']),
    ...mapGetters(['getItem', 'getItemByNumber', 'getEnumValues']),
    ...mapGetters(['getBatteryConnectionTypes', 'getGridAndPlantProtectionTypes']),
    ...mapGetters(['getItemSettings']),

    buttons() {
      return {
        showPreviousButton: this.item && this.item.previousItemNumber ? true : false,
        showNextButton: this.item && this.item.nextItemNumber ? true : false,
        showDeleteButton: this.item && this.item.number !== '_new' ? true : false,
        // showDeleteButton: false,
      };
    },
    optionsYesNo() {
      return yesNo();
    },
    item() {
      return this.getItemByNumber(this.number);
    },
    isNew() {
      return this.item._id === '_new';
    },
    breadcrumbs() {
      return [
        { name: 'Home', route: { name: 'home' } },
        { name: this.$t('item'), route: { name: 'ItemsPage' } },
        { name: this.item.number !== '_new' ? this.$t('edit') : this.$t('new') },
      ];
    },
    itemSettings() {
      return this.getItemSettings || {};
    },
  },
  methods: {
    ...mapActions([
      'initItem',
      'fetchItems',
      'fetchItemByNumber',
      'updateItem',
      'createItem',
      'deleteItem',
      'fetchEnumValues',
      'uploadItemAttachment',
      'fetchConnectionTypes',
      'deleteItemAttachment',
      'fetchBatteryConnectionTypes',
      'fetchGridAndPlantProtectionTypes',
      'fetchEnumValues',
      'fetchSettingsByCategory',
    ]),
    yesNoColor(state) {
      return getYesNoStateColor(state);
    },
    formatCurrency(value) {
      if (value === null || value === undefined) return '-';
      return new Intl.NumberFormat('de-DE', {
        style: 'currency',
        currency: 'EUR',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(value);
    },
    formatDateTime(dateString) {
      if (!dateString) return '-';
      return new Intl.DateTimeFormat('de-DE', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      }).format(new Date(dateString));
    },
    onDeleteItemAttachment(event) {
      try {
        if (!this.item || !this.item._id) {
          console.error('Cannot delete attachment: item or item._id is undefined');
          return;
        }

        this.deleteItemAttachment({
          parentId: this.item._id,
          attachmentId: event.attachmentId,
          attachmentType: event.attachmentType,
        });
      } catch (error) {
        console.error('Error deleting item attachment:', error);
      }
    },
    async setup() {
      await this.fetchEnumValues('ItemUnit');
      await this.fetchEnumValues('ItemType');
      await this.fetchEnumValues('ItemSolarPlant');
      this.itemAttachments = this.item?.itemAttachments || [];
    },
  },
  async mounted() {
    await this.fetchBatteryConnectionTypes();
    await this.fetchGridAndPlantProtectionTypes();
    await this.fetchConnectionTypes();
    await this.fetchEnumValues('YesNo');
    await this.fetchEnumValues('WallboxConnectionType');
    await this.fetchEnumValues('ShiftFactorCosphiType');
    await this.fetchEnumValues('ChargingType');
    await this.fetchSettingsByCategory('ITEM');
    this.$store.subscribeAction({
      after: (action, state) => {
        if (action.type === 'uploadItemAttachment') {
          this.isAttachmentUploadComplete = true;
        }
      },
    });
    await this.setup();
    if (!this.item && this.itemNumber) {
      this.number = this.itemNumber;
      this.fetchItemByNumber(this.number);
    } else {
      this.initItem();
      this.number = '_new';
    }
    this.itemAttachments = this.item?.itemAttachments || [];
  },
  /**
   * if only parameters of the route have changed the component will be reused
   * we have to update all the data before the route updates
   * @param to
   * @param from
   * @param next
   */
  async beforeRouteUpdate(to, from, next) {
    // console.log('Route params', to.params);
    // console.log('Current project number', this.projectNumber);
    if (to.params.itemNumber && to.params.itemNumber !== this.itemNumber) {
      this.$store.commit('SET_ITEM_DEFAULT_STATE');
      this.number = to.params.itemNumber;
      await this.fetchItemByNumber(this.number);
      await this.setup();
      next();
    } else {
      next(false);
    }
  },
};
</script>

<style scoped lang="scss"></style>
